<script lang="ts" setup>
import { useResponsive } from "@composables"
import { useBigUnderlineEffect } from "@composables"
import { computed, useTemplateRef } from "vue"

const { isMobile, isTablet, isDesktop } = useResponsive()

const section = useTemplateRef("section")
useBigUnderlineEffect(section)

const brands = computed(() => {
    return [
        {
            name: "Certificats d'économie d'énergie",
            logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_200,q_auto,f_auto/v1745850516/cee_j77gwi.png",
            style: {
                width: "6.875rem",
                height: "2.6875rem",
            },
            animate: isTablet.value
                ? "animate-enter fade-in-10 animate-duration-1000 slide-in-from-l-2"
                : "animate-enter fade-in-10 animate-duration-1000 slide-in-from-b-2",
        },
        {
            name: "Ademe",
            logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_200,q_auto,f_auto/v1745850516/ademe_d8cive.png",
            style: {
                width: " 3.9375rem",
                height: "4.49175rem",
            },
            animate: isTablet.value
                ? "animate-enter fade-in-10 animate-duration-1000 slide-in-from-b-2"
                : "animate-enter fade-in-10 animate-duration-1000 slide-in-from-t-2",
        },
        {
            name: "EDF",
            logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_200,q_auto,f_auto/v1745850516/edf_milfra.png",
            style: {
                width: "5.375rem",
                height: "2.3125rem",
            },
            animate: isTablet.value
                ? "animate-enter fade-in-10 animate-duration-1000 slide-in-from-r-2"
                : "animate-enter fade-in-10 animate-duration-1000 slide-in-from-t-2",
        },
    ]
})
</script>

<template>
    <div class="hero-section" ref="section">
        <!-- Left side -->
        <div class="hero-section__left">
            <!-- illustration -->
            <img
                src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_550,q_auto/v1745850401/hero_section_sgkpos.svg"
                alt="Illustration WattWatchers"
                v-animateonscrollv2="
                    isDesktop && {
                        enterClass: isTablet
                            ? 'animate-enter fade-in-10 animate-duration-1000 slide-in-from-b-10'
                            : 'animate-enter fade-in-10 animate-duration-1000 slide-in-from-l-10',
                    }
                "
            />
            <!-- Logo -->
            <div class="hero-section__brands">
                <img
                    v-for="(brand, index) in brands"
                    :key="index"
                    :class="brand.animate"
                    :src="brand.logo"
                    :alt="brand.name"
                    :style="brand.style"
                    loading="lazy"
                />
            </div>
            <p
                v-if="isTablet"
                v-animateonscrollv2="
                    isDesktop && {
                        enterClass:
                            'animate-enter fade-in-10 animate-duration-1000 slide-in-from-b-8',
                    }
                "
                class="hero-section__description"
            >
                <strong>Zéro</strong> spam, <strong>zéro</strong> démarchage,
                <strong>100% gratuit</strong>, et en
                <strong>quelques clics</strong> !
            </p>
        </div>
        <!-- Right Side -->
        <div class="hero-section__right">
            <h1 class="hero-section__title">
                Analysez
                <span
                    :class="!isMobile && 'big-underline big-underline--warning'"
                    >l'impact réel</span
                >
                de vos
                <span
                    :class="!isMobile && 'big-underline big-underline--warning'"
                    >travaux</span
                ><br />
                et
                <span
                    :class="!isMobile && 'big-underline big-underline--success'"
                    >orientez mieux</span
                >
                les
                <span
                    :class="!isMobile && 'big-underline big-underline--success'"
                    >futures aides publiques</span
                >
            </h1>
            <h2
                v-animateonscrollv2="
                    isDesktop && {
                        enterClass:
                            'animate-enter animate-duration-1000 slide-in-from-b-8 fade-in-10',
                    }
                "
                class="hero-section__subtitle"
            >
                Faites partie d'une étude nationale clé pour l'avenir
                énergétique !
            </h2>

            <div class="hero-section__buttons" v-if="!isMobile">
                <BaseButton
                    label="En savoir plus"
                    as-child
                    v-slot="slotProps"
                    severity="secondary"
                >
                    <RouterLink
                        v-animateonscrollv2="
                            isDesktop && {
                                enterClass:
                                    'animate-enter animate-duration-1000 slide-in-from-l-8 fade-in-10',
                            }
                        "
                        to="#study"
                        :class="['w-full', slotProps.class]"
                    >
                        En savoir plus
                    </RouterLink>
                </BaseButton>
                <RouterLink
                    :to="{ path: '/formulaire', query: $route.query }"
                    v-animateonscrollv2="
                        isDesktop && {
                            enterClass:
                                'animate-enter animate-duration-1000 slide-in-from-r-8 fade-in-10',
                        }
                    "
                >
                    <BaseButton
                        icon="pi pi-arrow-circle-right"
                        icon-pos="right"
                        label="Je participe"
                    ></BaseButton>
                </RouterLink>
            </div>
            <p
                v-if="isDesktop"
                v-animateonscrollv2="
                    isDesktop && {
                        enterClass:
                            'animate-enter fade-in-10 animate-duration-1000 slide-in-from-b-8',
                    }
                "
                class="hero-section__description"
            >
                <strong>Zéro</strong> spam, <strong>zéro</strong> démarchage,
                <strong>100% gratuit</strong>, et en
                <strong>quelques clics</strong> !
            </p>
        </div>
    </div>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.hero-section {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    @media (--large-viewport) {
        flex-direction: column-reverse;
    }

    @media (--medium-viewport) {
        flex-direction: column;
    }

    .hero-section__left {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2rem;
        flex: 1 0 0;

        /* Sur mobile */
        @media (--medium-viewport) {
            gap: 1rem;
        }
    }

    .hero-section__right {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2rem;
        flex: 1 0 0;

        /* Sur mobile */
        @media (--medium-viewport) {
            gap: 0.5rem;
        }
    }

    .hero-section__brands {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 2rem;
    }

    .hero-section__buttons {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        width: 100%;

        & > .p-button:first-child {
            width: 100%;
        }

        @media (--large-viewport) {
            & > .p-button {
                width: 100%;
            }
        }
    }

    .hero-section__title {
        color: #092754;
        font-size: 3rem;
        font-weight: 700;
        line-height: 125%;

        /* Sur mobile */
        @media (--medium-viewport) {
            font-size: 2.25rem;
        }
    }

    .hero-section__subtitle {
        color: #092754;
        font-size: 2rem;
        font-weight: 400;
        line-height: 125%;

        /* Sur mobile */
        @media (--medium-viewport) {
            font-size: 1.4rem;
        }
    }

    .hero-section__description {
        color: #092754;
        font-size: 0.77256rem;
        font-weight: 500;
        margin: 0 auto;
    }
}
</style>
